import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { PmxObject } from "@/Loader/Parser/pmxObject";
/**
 * Gives the effect of being in a child parent relationship regardless of the bone hierarchy. Similar to Unity's Parent constraint
 */
export class AppendTransformSolver {
    isLocal;
    /**
     * Whether to affect bone rotation
     */
    affectRotation;
    /**
     * Whether to affect bone position
     */
    affectPosition;
    /**
     * The ratio of the effect (can be negative)
     */
    ratio;
    /**
     * Affecting bones
     *
     * Similar to parent in bone hierarchy
     */
    targetBone;
    /**
     * Append transformed position
     */
    appendPosition = Vector3.Zero();
    /**
     * Append transformed rotation
     */
    appendRotation = Quaternion.Identity();
    /**
     * Creates append transform solver
     * @param boneFlag bone flag of the bone to be affected
     * @param boneAppendTransformMetadata metadata of the bone to be affected
     * @param targetBone affecting bone
     */
    constructor(boneFlag, boneAppendTransformMetadata, targetBone) {
        this.isLocal = (boneFlag & PmxObject.Bone.Flag.LocalAppendTransform) !== 0;
        this.affectRotation = (boneFlag & PmxObject.Bone.Flag.HasAppendRotate) !== 0;
        this.affectPosition = (boneFlag & PmxObject.Bone.Flag.HasAppendMove) !== 0;
        this.ratio = boneAppendTransformMetadata.ratio;
        this.targetBone = targetBone;
    }
    static _IdentityQuaternion = Quaternion.Identity();
    static _TargetBoneWorldMatrix = Matrix.Identity();
    /**
     * Updates the solver
     * @param animatedRotation rotation animation evaluation result with bone morphing applied
     * @param animatedPositionOffset position animation evaluation result with bone morphing applied
     */
    update(animatedRotation, animatedPositionOffset) {
        const targetBone = this.targetBone;
        if (this.affectRotation) {
            const appendRotation = this.appendRotation;
            if (this.isLocal) {
                const targetBoneWorldMatrix = targetBone.getWorldMatrixToRef(AppendTransformSolver._TargetBoneWorldMatrix);
                Quaternion.FromRotationMatrixToRef(targetBoneWorldMatrix, appendRotation);
            }
            else {
                if (targetBone.appendTransformSolver !== null && targetBone.appendTransformSolver.affectRotation) {
                    appendRotation.copyFrom(targetBone.appendTransformSolver.appendRotation);
                }
                else {
                    targetBone.getAnimatedRotationToRef(appendRotation);
                }
            }
            if (targetBone.ikChainInfo !== null && !this.isLocal) {
                targetBone.ikChainInfo.ikRotation.multiplyToRef(appendRotation, appendRotation);
            }
            if (this.ratio !== 1) {
                Quaternion.SlerpToRef(AppendTransformSolver._IdentityQuaternion, appendRotation, this.ratio, appendRotation);
            }
            animatedRotation.multiplyToRef(appendRotation, appendRotation);
        }
        if (this.affectPosition) {
            const appendPosition = this.appendPosition;
            if (this.isLocal) {
                const targetBoneWorldMatrix = targetBone.getWorldMatrixToRef(AppendTransformSolver._TargetBoneWorldMatrix);
                targetBoneWorldMatrix.multiplyToRef(targetBone.linkedBone.getAbsoluteInverseBindMatrix(), targetBoneWorldMatrix);
                targetBoneWorldMatrix.getTranslationToRef(appendPosition);
            }
            else {
                if (targetBone.appendTransformSolver !== null && targetBone.appendTransformSolver.affectPosition) {
                    appendPosition.copyFrom(targetBone.appendTransformSolver.appendPosition);
                }
                else {
                    targetBone.getAnimationPositionOffsetToRef(appendPosition);
                }
            }
            if (this.ratio !== 1) {
                appendPosition.scaleInPlace(this.ratio);
            }
            appendPosition.addInPlace(animatedPositionOffset);
        }
    }
}
